import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import api from '../utils/api'
import { WHATSAPP_SETTINGS_EVENT_REFRESH } from '../constants/whatsappSettingsKeys'
import { redactPersonalWhatsAppE164, redactPersonalWhatsAppUrl } from '../utils/whatsappNumbersRedact'
import { isWhatsAppHttpUrl, normalizeWhatsAppLinkInput, normalizeWhatsAppPhoneDigits, resolveWhatsAppHref } from '../utils/whatsappLink'

const WHATSAPP_KEYS = ['whatsappNumberSignup', 'whatsappNumberDepositWithdraw', 'whatsappNumberSupport'] as const
export type WhatsAppNumberKey = typeof WHATSAPP_KEYS[number]

export type WhatsAppNumbers = {
  whatsappNumberSignup: string
  whatsappNumberDepositWithdraw: string
  whatsappNumberSupport: string
}

const defaultNumbers: WhatsAppNumbers = {
  whatsappNumberSignup: '',
  whatsappNumberDepositWithdraw: '',
  whatsappNumberSupport: ''
}

/** Store digits as international E.164 (country code + number) */
function normalizeStoredPhone(value: string): string {
  return normalizeWhatsAppPhoneDigits(value)
}

function applySettingsToNumbers(settings: any[]): WhatsAppNumbers {
  const next = { ...defaultNumbers }
  WHATSAPP_KEYS.forEach((key) => {
    const s = settings.find((x: any) => x.name === key)
    const raw = s?.value ? String(s.value).trim() : ''
    if (!raw) {
      next[key] = ''
    } else if (isWhatsAppHttpUrl(raw)) {
      next[key] = redactPersonalWhatsAppUrl(raw)
    } else {
      next[key] = redactPersonalWhatsAppE164(normalizeStoredPhone(raw))
    }
  })
  return next
}

type ContextValue = {
  numbers: WhatsAppNumbers
  loading: boolean
  refresh: () => void
  getWhatsAppUrl: (key: WhatsAppNumberKey, prefillText?: string) => string
  getSupportUrl: () => string
  getSignupUrl: () => string
  getDepositWithdrawUrl: (prefillText?: string) => string
}

const WhatsAppNumbersContext = createContext<ContextValue>({
  numbers: defaultNumbers,
  loading: true,
  refresh: () => {},
  getWhatsAppUrl: () => 'https://api.whatsapp.com/send?phone=',
  getSupportUrl: () => 'https://api.whatsapp.com/send?phone=',
  getSignupUrl: () => 'https://api.whatsapp.com/send?phone=',
  getDepositWithdrawUrl: () => 'https://api.whatsapp.com/send?phone='
})

export function WhatsAppNumbersProvider({ children }: { children: React.ReactNode }) {
  const [numbers, setNumbers] = useState<WhatsAppNumbers>(defaultNumbers)
  const [loading, setLoading] = useState(true)

  const loadSettings = useCallback(() => {
    setLoading(true)
    api.get('setting-list')
      .then((res: any) => {
        const settings = res?.data?.data?.settings || res?.data?.settings || []
        if (Array.isArray(settings)) {
          setNumbers(applySettingsToNumbers(settings))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    const onRefresh = () => loadSettings()
    window.addEventListener(WHATSAPP_SETTINGS_EVENT_REFRESH, onRefresh)
    return () => window.removeEventListener(WHATSAPP_SETTINGS_EVENT_REFRESH, onRefresh)
  }, [loadSettings])

  const getWhatsAppUrl = useCallback((key: WhatsAppNumberKey, prefillText?: string) => {
    const stored = numbers[key]
    return resolveWhatsAppHref(stored, prefillText)
  }, [numbers])

  const getSupportUrl = useCallback(() => getWhatsAppUrl('whatsappNumberSupport'), [getWhatsAppUrl])
  const getSignupUrl = useCallback(() => getWhatsAppUrl('whatsappNumberSignup'), [getWhatsAppUrl])
  const getDepositWithdrawUrl = useCallback(
    (prefillText?: string) => getWhatsAppUrl('whatsappNumberDepositWithdraw', prefillText),
    [getWhatsAppUrl],
  )

  const value: ContextValue = {
    numbers,
    loading,
    refresh: loadSettings,
    getWhatsAppUrl,
    getSupportUrl,
    getSignupUrl,
    getDepositWithdrawUrl
  }

  return (
    <WhatsAppNumbersContext.Provider value={value}>
      {children}
    </WhatsAppNumbersContext.Provider>
  )
}

export function useWhatsAppNumbers(): ContextValue {
  return useContext(WhatsAppNumbersContext)
}
